/**
 * Pestaña Zonas: mapa de calor de la matriz zona de salida × zona de llegada,
 * coloreada por eficiencia. Con el filtro por camiseta responde la pregunta de
 * scouting: "¿desde dónde y hacia dónde le pega el #14?".
 *
 * ## Tres mapas, uno por fundamento
 *
 * El selector de arriba elige qué se mapea; las coordenadas se leen distinto en
 * cada caso y por eso los rótulos de los ejes cambian con él:
 *
 * | fundamento | filas | columnas | «perfectos» |
 * |---|---|---|---|
 * | Ataque | desde dónde remató | dónde cayó | kill% |
 * | Saque | desde dónde sacó | a qué zona mandó | ace% |
 * | Recepción | de dónde vino el saque | dónde se recibió | positividad (# + +) |
 *
 * La rampa es secuencial sobre el mínimo y el máximo OBSERVADOS en la propia
 * matriz: con muestras chicas una escala fija (−100%..+100%) pintaría todo del
 * mismo color y no diría nada. El número exacto va escrito en la celda y
 * repetido en el tooltip.
 */
import { useState } from 'react';
import { HeatLegend, Heatmap, type HeatCell } from '../../components/Charts';
import { Court, heatTone } from '../../components/Court';
import { matrixByZone } from '../../lib/court';
import { Card } from '../../components/States';
import type { MessageKey } from '../../i18n/index';
import { useT } from '../../i18n/use-t';
import { extent, zoneAxis } from '../../lib/charts';
import { effPct, pct } from '../../lib/format';
import { ZONE_SKILLS, type TeamZoneMatrix, type ZoneMatrix, type ZoneSkill } from '../../lib/projections';

/** Rótulos que cambian con el fundamento mapeado. */
const SKILL_KEYS: Record<ZoneSkill, {
  name: MessageKey;
  intro: MessageKey;
  total: MessageKey;
  perfect: MessageKey;
  perfectPct: MessageKey;
  emptyTeam: MessageKey;
  emptyPlayer: MessageKey;
  playerFilter: MessageKey;
  axisRow: MessageKey;
  axisCol: MessageKey;
  cellTitle: MessageKey;
}> = {
  attack: {
    name: 'zones.skillAttack',
    intro: 'zones.introAttack',
    total: 'zones.attacks',
    perfect: 'zones.kills',
    perfectPct: 'zones.killPct',
    emptyTeam: 'zones.emptyTeam',
    emptyPlayer: 'zones.emptyPlayer',
    playerFilter: 'zones.playerFilter',
    axisRow: 'zones.axisRow',
    axisCol: 'zones.axisCol',
    cellTitle: 'zones.cellTitle',
  },
  serve: {
    name: 'zones.skillServe',
    intro: 'zones.introServe',
    total: 'zones.serves',
    perfect: 'zones.aces',
    perfectPct: 'zones.acePct',
    emptyTeam: 'zones.emptyTeamServe',
    emptyPlayer: 'zones.emptyPlayerServe',
    playerFilter: 'zones.serverFilter',
    axisRow: 'zones.axisRowServe',
    axisCol: 'zones.axisColServe',
    cellTitle: 'zones.cellTitleServe',
  },
  reception: {
    name: 'zones.skillReception',
    intro: 'zones.introReception',
    total: 'zones.receptions',
    perfect: 'zones.perfectReceptions',
    perfectPct: 'zones.positivityPct',
    emptyTeam: 'zones.emptyTeamReception',
    emptyPlayer: 'zones.emptyPlayerReception',
    playerFilter: 'zones.receiverFilter',
    axisRow: 'zones.axisRowReception',
    axisCol: 'zones.axisColReception',
    cellTitle: 'zones.cellTitleReception',
  },
};

function TeamZones({
  team,
  side,
  skill,
  players,
  player,
  onPlayer,
}: {
  team: TeamZoneMatrix;
  side: 'home' | 'away';
  skill: ZoneSkill;
  players: readonly number[];
  player: number | null;
  onPlayer: (value: number | null) => void;
}): JSX.Element {
  const t = useT();
  const keys = SKILL_KEYS[skill];
  const noZone = t('zones.noZoneData');
  const cells: HeatCell[] = team.cells.map((cell) => ({
    row: cell.startZone,
    col: cell.endZone,
    value: cell.efficiency,
    count: cell.total,
    title: t(keys.cellTitle, {
      startZone: cell.startZone ?? noZone,
      endZone: cell.endZone ?? noZone,
      total: cell.total,
      kills: cell.kills,
      errors: cell.errors,
      blocked: cell.blocked,
      efficiency: effPct(cell.efficiency),
    }),
  }));
  const [min, max] = extent(cells.map((c) => c.value));

  return (
    <Card
      title={<span className={`side-name ${side} team-title`}>{team.name}</span>}
      actions={
        <label className="row" style={{ gap: 6 }}>
          <span className="hint">{t(keys.playerFilter)}</span>
          <select
            value={player === null ? '' : String(player)}
            onChange={(e) => onPlayer(e.target.value === '' ? null : Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value="">{t('zones.wholeTeam')}</option>
            {players.map((number) => (
              <option key={number} value={number}>
                #{number}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {team.total === 0 ? (
        <p className="empty">
          {player === null ? t(keys.emptyTeam) : t(keys.emptyPlayer, { player })}
        </p>
      ) : (
        <>
          <div className="row" style={{ gap: 10, marginBottom: 8 }}>
            <span className="chip muted">{t(keys.total, { count: team.total })}</span>
            <span className="chip muted">{t(keys.perfect, { count: team.kills })}</span>
            <span className="chip muted">{t('zones.errors', { count: team.errors })}</span>
            {/* En saque y recepción el grado `/` casi no se usa: no se muestra
                un chip en cero para no ensuciar la lectura. */}
            {skill === 'attack' || team.blocked > 0 ? (
              <span className="chip muted">{t('zones.blocked', { count: team.blocked })}</span>
            ) : null}
            <span className="chip muted">
              {t(keys.perfectPct, { pct: pct(team.perfectPct) })}
            </span>
            <span className={`chip ${team.efficiency < 0 ? 'bad' : 'ok'}`}>
              {t('zones.efficiency', { pct: effPct(team.efficiency) })}
            </span>
          </div>
          <ZonesCourt team={team} side={side} skill={skill} />
          <h3 style={{ marginTop: 16 }}>{t('matchViews.zonesMatrixTitle')}</h3>
          <div className="table-wrap">
            <Heatmap
              rows={zoneAxis(team.cells.map((c) => c.startZone))}
              cols={zoneAxis(team.cells.map((c) => c.endZone))}
              cells={cells}
              min={min}
              max={max}
              rowLabel={t(keys.axisRow)}
              colLabel={t(keys.axisCol)}
            />
          </div>
          <HeatLegend min={min} max={max} />
        </>
      )}
    </Card>
  );
}

export function ZonesTab({
  matrix,
  skill,
  onSkill,
  homePlayers,
  awayPlayers,
  homePlayer,
  awayPlayer,
  onHomePlayer,
  onAwayPlayer,
}: {
  matrix: ZoneMatrix;
  skill: ZoneSkill;
  onSkill: (value: ZoneSkill) => void;
  homePlayers: readonly number[];
  awayPlayers: readonly number[];
  homePlayer: number | null;
  awayPlayer: number | null;
  onHomePlayer: (value: number | null) => void;
  onAwayPlayer: (value: number | null) => void;
}): JSX.Element {
  const t = useT();
  return (
    <div>
      <div className="row" style={{ gap: 10, marginTop: 0, marginBottom: 10 }}>
        <label className="row" style={{ gap: 6 }}>
          <span className="hint">{t('zones.skillFilter')}</span>
          <select
            value={skill}
            onChange={(e) => onSkill(e.target.value as ZoneSkill)}
            style={{ width: 'auto' }}
          >
            {ZONE_SKILLS.map((name) => (
              <option key={name} value={name}>
                {t(SKILL_KEYS[name].name)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        {t(SKILL_KEYS[skill].intro)} {t('zones.intro')}
      </p>
      <TeamZones
        team={matrix.home}
        side="home"
        skill={skill}
        players={homePlayers}
        player={homePlayer}
        onPlayer={onHomePlayer}
      />
      <TeamZones
        team={matrix.away}
        side="away"
        skill={skill}
        players={awayPlayers}
        player={awayPlayer}
        onPlayer={onAwayPlayer}
      />
    </div>
  );
}

/**
 * La matriz dibujada como cancha completa. Abajo, el lado propio con la zona de
 * SALIDA (desde dónde remató o sacó) pintada por eficiencia; arriba, la cancha
 * rival con la LLEGADA, pintada por cantidad. Tocar una zona de salida filtra
 * la llegada: "¿adónde le pega el 4 cuando sale por zona 2?".
 *
 * En recepción las coordenadas van al revés (el saque sale de la cancha rival y
 * se recibe en la propia), así que la mitad propia muestra la zona donde se
 * RECIBIÓ y la rival, de dónde vino el saque.
 */
function ZonesCourt({
  team,
  side,
  skill,
}: {
  team: TeamZoneMatrix;
  side: 'home' | 'away';
  skill: ZoneSkill;
}): JSX.Element {
  const t = useT();
  const [picked, setPicked] = useState<number | null>(null);
  const ownAxis = skill === 'reception' ? 'end' : 'start';
  const otherAxis = skill === 'reception' ? 'start' : 'end';
  const rival = side === 'home' ? 'away' : 'home';

  const own = matrixByZone(team.cells, ownAxis);
  const filter = ownAxis === 'start' ? { start: picked } : { end: picked };
  const other = matrixByZone(team.cells, otherAxis, filter);

  const effs = own.zones.map((z) => z.efficiency);
  const [effMin, effMax] = effs.length > 0 ? [Math.min(...effs), Math.max(...effs)] : [0, 0];
  const countMax = Math.max(0, ...other.zones.map((z) => z.total));

  const ownZones = own.zones.map((z) => ({
    zone: z.zone,
    value: effPct(z.efficiency),
    sub: t('matchViews.zonesCount', { count: z.total }),
    ...heatTone(z.efficiency, effMin, effMax),
    title: t('matchViews.zonesOwnTitle', { zone: z.zone, count: z.total, eff: effPct(z.efficiency) }),
  }));
  const otherZones = other.zones.map((z) => ({
    zone: z.zone,
    value: String(z.total),
    sub: pct(other.total === 0 ? 0 : z.total / other.total),
    ...heatTone(z.total, 0, countMax),
    title: t('matchViews.zonesOtherTitle', { zone: z.zone, count: z.total }),
  }));

  return (
    <div className="court-layout" style={{ marginBottom: 6 }}>
      <Court
        label={t('matchViews.zonesCourtLabel', { team: team.name, skill: t(SKILL_KEYS[skill].name) })}
        sides={[
          {
            team: side,
            zones: ownZones,
            zonePrefix: 'Z',
            selectedZone: picked,
            onZone: (zone) => setPicked((prev) => (prev === zone ? null : zone)),
          },
          { team: rival, zones: otherZones, zonePrefix: 'Z' },
        ]}
        width={260}
      />
      <div>
        <h3>{t('matchViews.zonesCourtTitle')}</h3>
        <p className="court-note" style={{ marginTop: 0 }}>
          {t(skill === 'reception' ? 'matchViews.zonesCourtHintReception' : 'matchViews.zonesCourtHint')}
        </p>
        <div className="row" style={{ gap: 8 }}>
          {picked !== null ? (
            <>
              <span className="chip ok">{t('matchViews.zonesFiltering', { zone: picked })}</span>
              <button type="button" className="small" onClick={() => setPicked(null)}>
                {t('matchViews.zonesClear')}
              </button>
            </>
          ) : (
            <span className="chip muted">{t('matchViews.zonesNoFilter')}</span>
          )}
        </div>
        {own.other > 0 || other.other > 0 ? (
          <p className="court-note">
            {t('matchViews.zonesOther', { count: Math.max(own.other, other.other) })}
          </p>
        ) : null}
        <HeatLegend min={effMin} max={effMax} />
      </div>
    </div>
  );
}
